import { useCallback, useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import AnimatedPortal from "./AnimatedPortal";

const DISMISS_DISTANCE_PX = 80;
const DISMISS_VELOCITY = 0.5; // px/ms
/** Ignore tiny finger jitter so taps on buttons still fire click. */
const DRAG_LOCK_PX = 10;

export type BottomSheetProps = {
    visible: boolean;
    onClose: () => void;
    children: React.ReactNode;
    className?: string;
    zIndexClassName?: string;
    /** Show drag handle. Default true. */
    showHandle?: boolean;
    closeOnBackdrop?: boolean;
    /**
     * Where swipe-down may dismiss.
     * - `content` (default): handle, or sheet body when the outer scroller is at top
     *   and the gesture did not start on a nested scroller / control.
     * - `handle`: only the drag handle (use for sheets with their own scroll lists).
     */
    swipeDismiss?: "content" | "handle";
};

const INTERACTIVE_SELECTOR =
    "button,a,input,textarea,select,label,[role='button'],[role='link'],[role='checkbox'],[role='menuitem']";

function isScrollableY(el: HTMLElement): boolean {
    const { overflowY } = window.getComputedStyle(el);
    if (overflowY !== "auto" && overflowY !== "scroll" && overflowY !== "overlay") {
        return false;
    }
    return el.scrollHeight > el.clientHeight + 1;
}

/** True when the touch began on (or inside) a nested overflow scroller or a control. */
function shouldDeferGestureToTarget(
    target: EventTarget | null,
    scrollRoot: HTMLElement | null
): boolean {
    let el = target instanceof HTMLElement ? target : null;
    while (el && el !== scrollRoot) {
        if (el.matches?.(INTERACTIVE_SELECTOR)) return true;
        if (isScrollableY(el)) return true;
        el = el.parentElement;
    }
    return false;
}

/**
 * Bottom sheet: slides up from bottom with dimmed backdrop.
 * Swipe down to dismiss (from handle, or — when allowed — from content at top).
 */
const BottomSheet: React.FC<BottomSheetProps> = ({
    visible,
    onClose,
    children,
    className = "",
    zIndexClassName = "z-50",
    showHandle = true,
    closeOnBackdrop = true,
    swipeDismiss = "content",
}) => {
    const sheetRef = useRef<HTMLDivElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const dragStartY = useRef<number | null>(null);
    const dragStartTime = useRef<number>(0);
    const lastY = useRef<number>(0);
    const lastTime = useRef<number>(0);
    const dragLockedRef = useRef(false);
    const [dragOffset, setDragOffset] = useState(0);
    const [dragging, setDragging] = useState(false);
    const canDragRef = useRef(false);

    const resetDrag = useCallback(() => {
        setDragOffset(0);
        setDragging(false);
        dragStartY.current = null;
        canDragRef.current = false;
        dragLockedRef.current = false;
    }, []);

    useEffect(() => {
        if (!visible) resetDrag();
    }, [visible, resetDrag]);

    useEffect(() => {
        if (!visible) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [visible, onClose]);

    const onTouchStart = (e: React.TouchEvent, fromHandle: boolean) => {
        if (!fromHandle) {
            if (swipeDismiss === "handle") {
                canDragRef.current = false;
                return;
            }
            const scrollTop = scrollRef.current?.scrollTop ?? 0;
            if (scrollTop > 0) {
                canDragRef.current = false;
                return;
            }
            // Nested map lists / buttons live inside this scroller; don't steal their gestures.
            if (shouldDeferGestureToTarget(e.target, scrollRef.current)) {
                canDragRef.current = false;
                return;
            }
        }

        canDragRef.current = true;
        dragLockedRef.current = false;
        const y = e.touches[0].clientY;
        dragStartY.current = y;
        lastY.current = y;
        dragStartTime.current = Date.now();
        lastTime.current = dragStartTime.current;
        setDragging(false);
        setDragOffset(0);
    };

    const onTouchMove = (e: React.TouchEvent) => {
        if (dragStartY.current == null || !canDragRef.current) return;
        const y = e.touches[0].clientY;
        const delta = Math.max(0, y - dragStartY.current);
        lastY.current = y;
        lastTime.current = Date.now();

        // Wait for a clear downward drag before taking over — preserves button clicks.
        if (!dragLockedRef.current) {
            if (delta < DRAG_LOCK_PX) return;
            dragLockedRef.current = true;
            setDragging(true);
        }

        setDragOffset(delta);
        e.preventDefault();
    };

    const onTouchEnd = () => {
        if (dragStartY.current == null || !canDragRef.current) {
            resetDrag();
            return;
        }

        if (dragLockedRef.current) {
            const elapsed = Math.max(1, lastTime.current - dragStartTime.current);
            const velocity = dragOffset / elapsed;
            const shouldDismiss =
                dragOffset >= DISMISS_DISTANCE_PX || velocity >= DISMISS_VELOCITY;
            if (shouldDismiss) {
                onClose();
            }
        }
        resetDrag();
    };

    return (
        <AnimatedPortal visible={visible}>
            {(state) => (
                <div
                    className={twMerge(
                        "fixed inset-0 flex items-end justify-center",
                        zIndexClassName
                    )}
                    role="presentation"
                    onClick={(e) => {
                        if (closeOnBackdrop && e.target === e.currentTarget) onClose();
                    }}
                >
                    <div
                        className="ios-backdrop pointer-events-none"
                        data-state={state}
                        aria-hidden
                    />
                    <div
                        ref={sheetRef}
                        className={twMerge(
                            "ios-sheet-panel z-10 flex w-full max-h-[90vh] flex-col rounded-t-r10 bg-black100",
                            className
                        )}
                        data-state={state}
                        data-dragging={dragging ? "true" : "false"}
                        style={
                            dragging || dragOffset > 0
                                ? { transform: `translateY(${dragOffset}px)` }
                                : undefined
                        }
                        role="dialog"
                        aria-modal="true"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {showHandle && (
                            <div
                                className="flex shrink-0 cursor-grab items-center justify-center py-3 active:cursor-grabbing"
                                onTouchStart={(e) => onTouchStart(e, true)}
                                onTouchMove={onTouchMove}
                                onTouchEnd={onTouchEnd}
                                onTouchCancel={resetDrag}
                            >
                                <div className="h-1 w-10 rounded-full bg-black60" />
                            </div>
                        )}
                        <div
                            ref={scrollRef}
                            className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
                            onTouchStart={(e) => onTouchStart(e, false)}
                            onTouchMove={onTouchMove}
                            onTouchEnd={onTouchEnd}
                            onTouchCancel={resetDrag}
                        >
                            {children}
                        </div>
                    </div>
                </div>
            )}
        </AnimatedPortal>
    );
};

export default BottomSheet;
